import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import TechnologyStack from './TechnologyStack';
import AboutBG from '../img/about_bg.png'

// Keyframes for animations
const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInFromRight = keyframes`
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AboutContainer = styled.section`
  position: relative;
  padding: 5rem 2rem;
  width: 100%;
  margin: 0 auto;
  font-family: 'Arial', sans-serif;
  color: #f9f9f9; /* Light text for contrast */
  border-radius: 12px;

  /* Background image with gradient overlay */
  background: 
    linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), 
    url(${AboutBG});
  background-size: cover;
  background-position: center;
  background-attachment: fixed; /* Parallax effect */
  background-repeat: no-repeat;

  /* Subtle shadow to make section pop */
  box-shadow: 0 8px 25px rgba(0,0,0,0.4);
`;


const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #ffffff; /* Changed color to white */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #3498db, #2c3e50);
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.0rem;
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 0.5rem;
  text-align: justify;
  max-width: 1200px;
  
  ${props => props.animateLeft && css`
    opacity: 0;
    transform: translateX(-100px);
    transition: all 0.8s ease-out;
    
    &.animate {
      animation: ${slideInFromLeft} 0.8s forwards;
    }
  `}
  
  ${props => props.animateRight && css`
    opacity: 0;
    transform: translateX(100px);
    transition: all 0.8s ease-out;
    
    &.animate {
      animation: ${slideInFromRight} 0.8s forwards;
    }
  `}
`;

const HighlightedText = styled.span`
  color: #64b5f6; /* Adjusted highlight color for better visibility */
  font-weight: bold;
`;

const AboutUsComponent = () => {
  const paragraphRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    paragraphRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      paragraphRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const addToRefs = (el, index) => {
    if (el && !paragraphRefs.current.includes(el)) {
      paragraphRefs.current[index] = el;
    }
  };

  return (
    <AboutContainer id="about-us">
      <SectionTitle>About Us – SRK Software Solutions</SectionTitle>
      <ContentWrapper>
        <Paragraph 
          ref={(el) => addToRefs(el, 0)} 
          animateLeft
        >
          SRK Software Solutions is a premier provider of digital marketing and IT services in India, 
          committed to delivering purpose-driven strategies and measurable business outcomes. 
          Established in 2020, we have grown from a small team of passionate professionals into a 
          trusted partner for organizations ranging from startups to Fortune 500 companies. 
          With offices in Noida, Jaipur, and Patna, we serve clients across diverse industries 
          with tailored, result-oriented solutions.
        </Paragraph>
        
        <Paragraph 
          ref={(el) => addToRefs(el, 1)} 
          animateRight
        >
          Our expertise lies in offering comprehensive 360° digital marketing services, including 
          <HighlightedText> Search Engine Optimization (SEO)</HighlightedText>, content marketing, 
          paid advertising, social media management, local search marketing, and website development. 
          As a leading SEO and digital marketing company in India, we focus on transforming your 
          digital presence into a powerful tool for lead generation, customer engagement, and revenue growth.
        </Paragraph>
        
        <Paragraph 
          ref={(el) => addToRefs(el, 2)} 
          animateLeft
        >
          Beyond digital marketing, we specialize in technology-driven solutions encompassing 
          consulting, domain-specific development, design, and continuous support. By harnessing 
          advanced platforms, frameworks, and databases, we ensure that our clients' strategies 
          remain agile, efficient, and aligned with the rapidly evolving digital landscape.
        </Paragraph>
        
        <Paragraph 
          ref={(el) => addToRefs(el, 3)} 
          animateRight
        >
          At SRK Software Solutions, we believe in simplifying complexity through data-driven 
          insights and integrated planning. Our approach combines creative storytelling, analytical 
          rigor, and cutting-edge technology to craft campaigns and solutions that not only meet 
          but exceed client expectations.
        </Paragraph>
        
        <Paragraph 
          ref={(el) => addToRefs(el, 4)} 
          animateLeft
        >
          With a focus on innovation, transparency, and long-term partnerships, we are dedicated 
          to helping businesses scale effectively, achieve sustainable growth, and maximize ROI.
        </Paragraph>
        
        <Paragraph 
          ref={(el) => addToRefs(el, 5)} 
          animateRight
        >
          <HighlightedText>SRK Software Solutions</HighlightedText> – Driving Digital Excellence, 
          Empowering Business Success.
        </Paragraph>
        <TechnologyStack/>
      </ContentWrapper>
    </AboutContainer>
  );
};

export default AboutUsComponent;